'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { signIn, signOut, useSession } from 'next-auth/react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/libs/api';
import publicApi from '@/libs/publicApi';
import { toastError } from '@/components/common/Others/ToastMessage';
import { useConfirm } from '@/components/common/ConfirmModal/ConfirmProvider';
import { useWebCommonStore } from '@/stores/useWebCommonStore';
import CartIcon from '@/components/shop/CartIcon/CartIcon';
import styles from './HamburgerDrawer.module.scss';

interface RecentProduct {
  id: number;
  prodNm: string;
  sysFileNm?: string;
  lastViewTm?: string;
}

function formatViewDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (target.getTime() === today.getTime()) return '오늘';
  if (target.getTime() === yesterday.getTime()) return '어제';
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}/${mm}/${dd}`;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const MENU_GROUPS = [
  {
    title: '마이페이지',
    items: [
      { label: '주문내역', href: '/mypage/orders', authRequired: true },
      { label: '장바구니', href: '/cart', authRequired: false },
    ],
  },
  {
    title: '고객센터',
    items: [],
  },
];

const CS_GROUP_TITLE = '고객센터';

export default function HamburgerDrawer({ isOpen, onClose }: Props) {
  const { data: session } = useSession();
  const router = useRouter();
  const getFileUrl = useWebCommonStore((s) => s.getFileUrl);
  const [recentProducts, setRecentProducts] = useState<(RecentProduct & { imgUrl?: string })[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    publicApi.get('/frontWeb/pageViewHistory/recentProducts').then(async ({ data }) => {
      if (data?.resultCode !== 200 || !data.body?.length) { setRecentProducts([]); return; }
      const items: RecentProduct[] = data.body;
      const withImg = await Promise.all(
        items.map(async (p) => ({ ...p, imgUrl: p.sysFileNm ? await getFileUrl(p.sysFileNm) : undefined }))
      );
      setRecentProducts(withImg);
    }).catch(() => setRecentProducts([]));
  }, [isOpen]);
  // 드로어 열릴 때 배경 스크롤 잠금
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC 키로 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const confirm = useConfirm();

  const handleLogout = async () => {
    await signOut({ redirect: false });
    onClose();
    router.push('/');
  };

  const withdrawMutation = useMutation({
    mutationFn: async () => {
      const socialAccountId = (session as any)?.socialAccountId;
      const { data } = await authApi.delete('/frontWeb/login/withdraw', { params: { socialAccountId } });
      if (data?.resultCode !== 200) throw new Error(data?.resultMessage ?? '탈퇴 처리 중 오류가 발생했습니다.');
    },
    onSuccess: async () => {
      await signOut({ callbackUrl: '/' });
    },
    onError: (error: any) => {
      toastError(error?.message ?? '탈퇴 처리 중 오류가 발생했습니다.');
    },
  });

  const handleWithdraw = async () => {
    const ok = await confirm({ message: '정말 탈퇴하시겠습니까?\n탈퇴 시 개인정보가 즉시 삭제되며 복구할 수 없습니다.' });
    if (!ok) return;
    withdrawMutation.mutate();
  };

  return (
    <>
      {/* 딤 배경 */}
      <div className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ''}`} onClick={onClose} aria-hidden="true" />

      {/* 드로어 패널 */}
      <aside className={`${styles.drawer} ${isOpen ? styles.drawerOpen : ''}`} aria-label="메뉴">
        {/* 상단: 카트 / 검색 / 닫기 */}
        <div className={styles.topBar}>
          <CartIcon className={styles.topIconCart} />

          <Link href="/search" className={styles.topIcon} onClick={onClose} aria-label="검색">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
              <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Link>

          <button className={styles.topIcon} onClick={onClose} aria-label="닫기">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* 로그인 상태에 따른 유저 영역 */}
        <div className={styles.userArea}>
          {session ? (
            <div className={styles.loggedIn}>
              <p className={styles.userName}>
                <strong>{session.user?.name ?? '회원'}</strong>님 안녕하세요 👋
              </p>
              <button className={styles.logoutBtn} onClick={handleLogout}>
                로그아웃
              </button>
            </div>
          ) : (
            <div className={styles.authBtns}>
              <Link href="/login" className={styles.loginBtn} onClick={onClose}>
                로그인
              </Link>
            </div>
          )}
        </div>

        {/* 메뉴 그룹 */}
        <nav className={styles.nav}>
          {MENU_GROUPS.map((group) => {
            const visibleItems = group.items.filter((item) => !item.authRequired || !!session);
            if (!session && group.title !== CS_GROUP_TITLE && visibleItems.length === 0) return null;
            return (
              <div key={group.title} className={styles.group}>
                <p className={styles.groupTitle}>{group.title}</p>
                <ul className={styles.groupList}>
                  {visibleItems.map((item) => (
                    <li key={item.href}>
                      <Link href={item.href} className={styles.menuItem} onClick={onClose}>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                  {/* 고객센터 그룹 하단에 탈퇴 버튼 — 로그인 상태에서만 */}
                  {session && group.title === CS_GROUP_TITLE && (
                    <li>
                      <button className={`${styles.menuItem} ${styles.withdrawBtn}`} onClick={handleWithdraw} disabled={withdrawMutation.isPending}>
                        회원 탈퇴
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* 최근 본 상품 */}
        {recentProducts.length > 0 && (
          <div className={styles.recentSection}>
            <p className={styles.recentTitle}>최근 본 상품</p>
            <div className={styles.recentList}>
              {recentProducts.map((p) => (
                <Link key={p.id} href={`/products/all/${p.id}`} className={styles.recentItem} onClick={onClose}>
                  <div className={styles.recentImgWrap}>
                    {p.imgUrl
                      ? <img src={p.imgUrl} alt={p.prodNm} className={styles.recentImg} />
                      : <div className={styles.recentImgPlaceholder} />}
                  </div>
                  <div className={styles.recentInfo}>
                    <p className={styles.recentNm}>{p.prodNm}</p>
                    <span className={styles.recentDate}>{formatViewDate(p.lastViewTm)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
